/*
 * Enhanced Map View Component
 * 
 * Interactive OpenStreetMap with enhanced features:
 * - Large mobile-friendly markers (35x57px regular, 40x65px selected)
 * - Subscribed buses list with real-time distance calculations
 * - Auto-locate button for user location
 * - Clickable filter cards for map layer control
 * - Real-time location updates via WebSocket
 * - Programmatic zoom and center control
 * - Automatic user location on component load
 * - Speed conversion from km/h to mph for US users
 * 
 * NEW FEATURES:
 * - Subscribed buses display with distance and time estimates
 * - Auto-locate functionality for user location
 * - Interactive filter cards to control map layers
 * - Enhanced mobile-first responsive design
 * - Smart automatic centering: user location OR specific vehicle/location focus
 * - Supports both {lat, lng} object and [lat, lng] array coordinate formats
 * - Speed display in mph (converted from km/h)
 */

import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import L from 'leaflet';
import { APP_CONSTANTS, getVehicleMarkerIcon, getUserLocationIcon, getTimeSinceUpdate, API } from '../../utils/config';
import MapController from './MapController';
import { OptimizedVehicleMarkers } from './VehicleClusterManager';
import TruckHistoryViewer from './TruckHistoryViewer';
import { useAuth } from '../../contexts/AuthContext';



/**
 * Convert km/h to mph
 * @param {number} kmh - Speed in kilometers per hour
 * @returns {number} Speed in miles per hour (rounded to 1 decimal place)
 */
const kmhToMph = (kmh) => {
  if (!kmh) return 0;
  return Math.round(kmh * 0.621371 * 10) / 10;
};

/**
 * Calculate bearing between two points
 * @param {Object} point1 - {lat, lng}
 * @param {Object} point2 - {lat, lng}
 * @returns {number} Bearing in degrees
 */
const calculateBearing = (point1, point2) => {
  const lat1 = point1.lat * Math.PI / 180;
  const lat2 = point2.lat * Math.PI / 180;
  const deltaLng = (point2.lng - point1.lng) * Math.PI / 180;

  const y = Math.sin(deltaLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);

  const bearing = Math.atan2(y, x) * 180 / Math.PI;
  return (bearing + 360) % 360;
};

/**
 * Generate arrow markers along route to show direction
 * @param {Array} coordinates - Array of {lat, lng} objects
 * @param {string} color - Route color
 * @returns {Array} Array of arrow marker data {position, rotation}
 */
const generateRouteArrows = (coordinates, color) => {
  if (coordinates.length < 2) return [];

  const arrows = [];
  const arrowInterval = 4; // Place arrow every 4 points

  for (let i = 0; i < coordinates.length - 1; i += arrowInterval) {
    const point1 = coordinates[i];
    const point2 = coordinates[i + 1];

    // Calculate midpoint between the two coordinates
    const midLat = (point1.lat + point2.lat) / 2;
    const midLng = (point1.lng + point2.lng) / 2;

    // Calculate bearing/rotation
    const rotation = calculateBearing(point1, point2);

    arrows.push({
      position: [midLat, midLng],
      rotation: rotation,
      color: color
    });
  }

  return arrows;
};

/**
 * Create directional arrow icon for route
 * @param {number} rotation - Rotation in degrees
 * @param {string} color - Arrow color
 * @returns {Object} Leaflet DivIcon
 */
const createRouteArrowIcon = (rotation, color) => {
  return L.divIcon({
    html: `
      <svg width="20" height="20" viewBox="0 0 20 20" style="transform: rotate(${rotation}deg);">
        <path
          d="M10 2 L14 10 L10 8 L6 10 Z"
          fill="${color}"
          stroke="white"
          stroke-width="1"
          opacity="0.8"
        />
      </svg>
    `,
    className: 'route-arrow-icon',
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
};

/**
 * Create stop marker icon for route stops
 * @param {string} color - Stop color (matches route color)
 * @param {number} sequence - Stop sequence number
 * @returns {Object} Leaflet DivIcon
 */
const createStopIcon = (color, sequence) => {
  return L.divIcon({
    html: `
      <div style="
        width: 28px;
        height: 28px;
        background-color: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: bold;
        color: white;
        text-shadow: 0 1px 2px rgba(0,0,0,0.5);
      ">
        ${sequence}
      </div>
    `,
    className: 'route-arrow-icon',
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
};

/**
 * Vehicle Marker Component
 * Renders individual vehicle markers with detailed popup information
 */
const VehicleMarker = ({ vehicle, selectedVehicleId, onVehicleSelect, getTimeSinceUpdate, onShowRoute, currentRoute }) => {
  if (!vehicle.last_location) return null;

  const position = [vehicle.last_location.latitude, vehicle.last_location.longitude];
  const icon = getVehicleMarkerIcon(vehicle, selectedVehicleId);
  const isVan = vehicle.vehicle_type === 'van';

  return (
    <Marker
      position={position}
      icon={icon}
      eventHandlers={{
        click: (e) => {
          // Prevent event from bubbling up and potentially triggering other map behaviors
          e.originalEvent?.stopPropagation();

          // Only select the vehicle, don't trigger any map centering
          onVehicleSelect(vehicle.id);
        }
      }}
    >
      <Popup>
        <div className="text-xs min-w-32 max-w-48">
          <div className="flex justify-between items-start mb-1">
            <h3 className="font-bold text-sm text-gray-900 leading-tight">
              {(vehicle.vehicle_type === 'bus' || vehicle.vehicle_type === 'truck')
                ? `${vehicle.make} ${vehicle.model}`
                : `${vehicle.year} ${vehicle.make} ${vehicle.model}`
              }
            </h3>
            <span className={`px-1 py-0.5 rounded text-xs font-medium ${vehicle.id === selectedVehicleId
              ? 'bg-red-100 text-red-800'
              : (vehicle.vehicle_type === 'bus' || vehicle.vehicle_type === 'truck')
                ? 'bg-green-100 text-green-800'
                : vehicle.vehicle_type === 'van'
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
              {vehicle.id === selectedVehicleId && '🎯'}
              {vehicle.id !== selectedVehicleId && (
                (vehicle.vehicle_type === 'bus' || vehicle.vehicle_type === 'truck') ? '🚛' :
                vehicle.vehicle_type === 'van' ? '🚐' : '🚗'
              )}
            </span>
          </div>

          <div className="space-y-1">
            <div className="grid grid-cols-2 gap-1 text-xs">
              <div>
                <p className="text-gray-600 text-xs"><strong>License:</strong></p>
                <p className="font-medium text-xs">{vehicle.license_plate}</p>
              </div>
              <div>
                <p className="text-gray-600 text-xs"><strong>Color:</strong></p>
                <p className="font-medium text-xs">{vehicle.color}</p>
              </div>
            </div>

            <div className="pt-1 border-t border-gray-200">
              {/* <p className="text-gray-600 text-xs"><strong>Location:</strong></p>
              <p className="text-xs font-mono">
                {vehicle.last_location.latitude?.toFixed(4)}, {vehicle.last_location.longitude?.toFixed(4)}
              </p> */}

              <p className="text-gray-500 text-xs">
                {(() => {
                  const timestamp = vehicle.last_updated;
                  if (!timestamp) return getTimeSinceUpdate(timestamp);

                  const now = new Date();
                  let updateTime;

                  if (typeof timestamp === 'string') {
                    if (timestamp.endsWith('Z')) {
                      updateTime = new Date(timestamp);
                    } else if (timestamp.includes('+00:00')) {
                      updateTime = new Date(timestamp);
                    } else if (timestamp.includes('T')) {
                      updateTime = new Date(timestamp + 'Z');
                    } else {
                      updateTime = new Date(timestamp);
                    }
                  } else {
                    updateTime = new Date(timestamp);
                  }

                  if (isNaN(updateTime.getTime())) {
                    return getTimeSinceUpdate(timestamp);
                  }

                  const diffMilliseconds = now.getTime() - updateTime.getTime();
                  const diffMinutes = Math.floor(diffMilliseconds / (1000 * 60));

                  // If more than 15 minutes, show as "Parked"
                  if (diffMinutes >= 15) {
                    if (diffMinutes < 60) {
                      return `🅿️ Parked for ${diffMinutes} mins`;
                    } else {
                      const hours = Math.floor(diffMinutes / 60);
                      const remainingMins = diffMinutes % 60;
                      if (hours === 1 && remainingMins === 0) {
                        return `🅿️ Parked for 1 hour`;
                      } else if (remainingMins === 0) {
                        return `🅿️ Parked for ${hours} hours`;
                      } else {
                        return `🅿️ Parked for ${hours}h ${remainingMins}m`;
                      }
                    }
                  }

                  // Less than 15 minutes, show normal time
                  return getTimeSinceUpdate(timestamp);
                })()}
              </p>
            </div>

            {vehicle.last_location.speed && (
              <div className="pt-1 border-t border-gray-200">
                <p className="text-gray-600 text-xs">
                  <strong>Speed:</strong> {kmhToMph(vehicle.last_location.speed)} mph
                </p>
                {vehicle.last_location.heading !== null && vehicle.last_location.heading !== undefined && (
                  <p className="text-gray-600 text-xs">
                    <strong>Direction:</strong> <span className="text-red-600 font-medium">
                      {(() => {
                        const h = vehicle.last_location.heading;
                        if (h >= 337.5 || h < 22.5) return 'North ↑';
                        if (h >= 22.5 && h < 67.5) return 'NE ↗';
                        if (h >= 67.5 && h < 112.5) return 'East →';
                        if (h >= 112.5 && h < 157.5) return 'SE ↘';
                        if (h >= 157.5 && h < 202.5) return 'South ↓';
                        if (h >= 202.5 && h < 247.5) return 'SW ↙';
                        if (h >= 247.5 && h < 292.5) return 'West ←';
                        if (h >= 292.5 && h < 337.5) return 'NW ↖';
                        return 'Unknown';
                      })()}
                    </span>
                  </p>
                )}
              </div>
            )}

            <div className="pt-1 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-xs font-medium capitalize">{vehicle.vehicle_type}</p>
                <p className="text-xs">{vehicle.is_public ? 'Public' : 'Private'}</p>
              </div>
            </div>

            {vehicle.id === selectedVehicleId && (
              <div className="pt-1 border-t border-red-200 bg-red-50 -mx-1 -mb-1 px-1 py-1 rounded-b">
                <p className="text-red-600 font-medium text-xs flex items-center">
                  🎯 Selected
                </p>
              </div>
            )}

            {/* Show Route button for vans */}
            {isVan && onShowRoute && (
              <div className="pt-2 border-t border-gray-200 mt-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowRoute(vehicle.id, currentRoute);
                  }}
                  className={`w-full px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                    currentRoute
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                >
                  {currentRoute ? '✕ Hide Route' : '🗺️ Show Route'}
                </button>
                {currentRoute && (
                  <div className="mt-1 text-xs text-gray-600 flex items-center">
                    <div
                      className="w-3 h-3 rounded-full mr-1"
                      style={{ backgroundColor: currentRoute.color }}
                    ></div>
                    {currentRoute.name}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

/**
* StopSwipeBubble Component
* Prevents swipe / pan events inside the map from bubbling up to the
* global swipe-to-change-tab handler.
*/
const StopSwipeBubble = ({ children }) => {
  const haltStart = (e) => e.stopPropagation();
  return (
    <div
      className="w-full h-full"
      onTouchStart={haltStart}
      onPointerDown={haltStart}
    >
      {children}
    </div>
  );
};

/**
 * Helper to check if bus should be hidden due to inactivity (60+ minutes)
 */
const shouldHideInactiveBus = (bus) => {
  if (!bus.last_updated) return false;

  const now = new Date();
  let updateTime;

  if (typeof bus.last_updated === 'string') {
    if (bus.last_updated.endsWith('Z')) {
      updateTime = new Date(bus.last_updated);
    } else if (bus.last_updated.includes('+00:00')) {
      updateTime = new Date(bus.last_updated);
    } else if (bus.last_updated.includes('T')) {
      updateTime = new Date(bus.last_updated + 'Z');
    } else {
      updateTime = new Date(bus.last_updated);
    }
  } else {
    updateTime = new Date(bus.last_updated);
  }

  if (isNaN(updateTime.getTime())) return false;

  const diffMilliseconds = now.getTime() - updateTime.getTime();
  const diffMinutes = Math.floor(diffMilliseconds / (1000 * 60));

  // Hide if inactive for 60+ minutes (1 hour)
  return diffMinutes >= 60;
};

/**
 * Subscribed Buses List Component
 * Displays subscribed buses with real-time distance and time estimates
 */
const SubscribedBusesList = ({ subscribedBuses = [], onBusClick, onCreateSubscription }) => {
  // Filter out buses that have been inactive for 60+ minutes
  const activeBuses = (subscribedBuses || []).filter(bus => !shouldHideInactiveBus(bus));
  const safeBuses = activeBuses;
  const [currentPage, setCurrentPage] = useState(0);
  const busesPerPage = 3;

  // Sort buses by distance (nearest first)
  const sortedBuses = [...safeBuses].sort((a, b) => {
    const distanceA = parseFloat(a.distance) || Infinity;
    const distanceB = parseFloat(b.distance) || Infinity;
    return distanceA - distanceB;
  });

  // Calculate pagination
  const totalPages = Math.ceil(sortedBuses.length / busesPerPage);
  const startIndex = currentPage * busesPerPage;
  const endIndex = startIndex + busesPerPage;
  const currentBuses = sortedBuses.slice(startIndex, endIndex);

  // Reset to first page when buses list changes
  useEffect(() => {
    setCurrentPage(0);
  }, [safeBuses.length]);

  if (safeBuses.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-yellow-600 text-xl mr-3">🚛</span>
            <div>
              <h3 className="font-medium text-yellow-800">No Garbage Truck Alerts</h3>
              <p className="text-sm text-yellow-700">
                Create alerts to see approaching garbage trucks here
              </p>
            </div>
          </div>
          <button
            onClick={onCreateSubscription}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center"
          >
            <span className="mr-2">📍</span>
            Create Alert
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md mb-6">
      <div className="p-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg text-gray-900 flex items-center">
              <span className="mr-2">🚛</span>
              Garbage Truck Alerts ({safeBuses.length})
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Garbage trucks approaching your locations (sorted by distance)
            </p>
          </div>
          {totalPages > 1 && (
            <div className="text-sm text-gray-600">
              Page {currentPage + 1} of {totalPages}
            </div>
          )}
        </div>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentBuses.map((bus, index) => (
            <div
              key={`${bus.id}-${index}`}
              onClick={() => onBusClick(bus)}
              className="border rounded-lg p-4 hover:shadow-lg hover:scale-105 cursor-pointer bg-green-50 border-green-200 transition-all duration-200"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">
                    {bus.make} {bus.model}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {bus.subscription.display_name}
                  </p>
                </div>
                <div className="text-right">
                  <div className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium mb-1">
                    🚛 Live
                  </div>
                  <div className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {bus.distance} mi
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Distance:</span>
                  <span className="text-sm font-medium">{bus.distance} miles</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Est. Time:</span>
                  <span className="text-sm font-medium text-blue-600">
                    ~{bus.timeEstimate} min
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Route:</span>
                  <span className="text-sm font-medium">
                    {bus.subscription.origin_town} → {bus.subscription.destination_town}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Status:</span>
                  <span className="text-sm font-medium text-green-600">
                    Approaching your location
                  </span>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-green-200">
                <p className="text-xs text-green-600 font-medium flex items-center">
                  <span className="mr-2">🗺️</span>
                  Click to track on map
                </p>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-6 space-x-4">
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${currentPage === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
            >
              ← Previous
            </button>

            <div className="flex items-center space-x-2">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i)}
                  className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${currentPage === i
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              disabled={currentPage === totalPages - 1}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${currentPage === totalPages - 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Map Filter Cards Component
 * Provides clickable cards to filter what's displayed on the map
 */
const MapFilterCards = ({ mapFilters, onFilterChange, publicVehicles, userVehicles, sharedVehicles }) => {
  // Helper to check if vehicle should be hidden due to inactivity
  const isInactiveForTooLong = (vehicle) => {
    if (!vehicle.last_updated) return false;

    const now = new Date();
    let updateTime;

    if (typeof vehicle.last_updated === 'string') {
      if (vehicle.last_updated.endsWith('Z')) {
        updateTime = new Date(vehicle.last_updated);
      } else if (vehicle.last_updated.includes('+00:00')) {
        updateTime = new Date(vehicle.last_updated);
      } else if (vehicle.last_updated.includes('T')) {
        updateTime = new Date(vehicle.last_updated + 'Z');
      } else {
        updateTime = new Date(vehicle.last_updated);
      }
    } else {
      updateTime = new Date(vehicle.last_updated);
    }

    if (isNaN(updateTime.getTime())) return false;

    const diffMilliseconds = now.getTime() - updateTime.getTime();
    const diffMinutes = Math.floor(diffMilliseconds / (1000 * 60));

    // Inactive for 60+ minutes (1 hour)
    return diffMinutes >= 60;
  };

  const filterCards = [
    {
      id: 'showBuses',
      label: 'Garbage Trucks',
      icon: '🚛',
      count: publicVehicles.filter(v =>
        (v.vehicle_type === 'bus' || v.vehicle_type === 'truck') &&
        v.is_active !== false &&
        !isInactiveForTooLong(v)
      ).length,
      color: 'green'
    }
    // Removed for garbageTracker: showVans (Shuttle Vans), showUserVehicles (My Vehicles)
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {filterCards.map(card => (
        <div
          key={card.id}
          onClick={() => onFilterChange(card.id, !mapFilters[card.id])}
          className={`rounded-lg p-4 cursor-pointer transition-all duration-200 transform hover:scale-105 ${mapFilters[card.id]
            ? card.color === 'green'
              ? 'bg-green-50 border-2 border-green-200 shadow-md'
              : card.color === 'orange'
                ? 'bg-orange-50 border-2 border-orange-200 shadow-md'
                : card.color === 'blue'
                  ? 'bg-blue-50 border-2 border-blue-200 shadow-md'
                  : 'bg-purple-50 border-2 border-purple-200 shadow-md'
            : 'bg-gray-50 border-2 border-gray-200 hover:bg-gray-100'
            }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="text-2xl mr-3">{card.icon}</div>
              <div>
                <h3 className={`font-semibold ${mapFilters[card.id]
                  ? card.color === 'green'
                    ? 'text-green-800'
                    : card.color === 'orange'
                      ? 'text-orange-800'
                      : card.color === 'blue'
                        ? 'text-blue-800'
                        : 'text-purple-800'
                  : 'text-gray-700'
                  }`}>
                  {card.label}
                </h3>
                <p className={`text-sm ${mapFilters[card.id]
                  ? card.color === 'green'
                    ? 'text-green-600'
                    : card.color === 'orange'
                      ? 'text-orange-600'
                      : card.color === 'blue'
                        ? 'text-blue-600'
                        : 'text-purple-600'
                  : 'text-gray-500'
                  }`}>
                  {card.count} trucks
                </p>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${mapFilters[card.id]
              ? card.color === 'green'
                ? 'bg-green-600 border-green-600'
                : card.color === 'orange'
                  ? 'bg-orange-600 border-orange-600'
                  : card.color === 'blue'
                    ? 'bg-blue-600 border-blue-600'
                    : 'bg-purple-600 border-purple-600'
              : 'border-gray-400'
              }`}>
              {mapFilters[card.id] && (
                <span className="text-white text-sm">✓</span>
              )}
            </div>
          </div>

          <div className="mt-3">
            <p className={`text-xs ${mapFilters[card.id]
              ? card.color === 'green'
                ? 'text-green-600'
                : card.color === 'orange'
                  ? 'text-orange-600'
                  : card.color === 'blue'
                    ? 'text-blue-600'
                    : 'text-purple-600'
              : 'text-gray-500'
              }`}>
              {mapFilters[card.id] ? 'Showing on map' : 'Click to show on map'}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Enhanced Map View Component
 * Main map interface with subscribed buses list and filtering
 * Now automatically centers on user location when component loads (only if no specific focus)
 * Handles both object {lat, lng} and array [lat, lng] formats for mapCenter
 */
const MapView = ({
  vehicles = [],
  publicVehicles = [],
  userVehicles = [],
  sharedVehicles = [],
  selectedVehicleId,
  mapCenter,
  mapZoom,
  userLocation,
  subscribedBusesWithDistance = [],
  mapFilters = {},
  onVehicleSelect,
  onAutoLocate,           // Centers map AND sets user location
  onLocateMeClick,        // NEW: Dedicated "Locate Me" button handler
  onSetUserLocation,      // NEW: Only sets user location (for marker)
  onFilterChange,
  onSubscribedBusClick,
  onCreateSubscription,
  enableClustering = true,  // NEW: Enable/disable clustering
  maxMarkersBeforeClustering = 50,  // NEW: Threshold for clustering
  shouldAutoCenter = false, // NEW: Explicit flag for when to auto-center
  setShouldAutoCenter,       // NEW: Function to reset auto-center flag
  enableSwipe,              // Function to enable swipe navigation
  disableSwipe              // Function to disable swipe navigation
}) => {

  // Get current user for feature access control
  const { user } = useAuth();
  const isDeveloper = user?.email === 'mark@mark-dev.com';

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHistoryViewer, setShowHistoryViewer] = useState(false);
  const [selectedTruckForHistory, setSelectedTruckForHistory] = useState(null);
  const [checkAllTrucksMode, setCheckAllTrucksMode] = useState(false);
  const mapRef = useRef();

  // Disable swipe navigation and lock body scroll when history viewer modal is open
  useEffect(() => {
    if (showHistoryViewer) {
      // Disable swipe navigation
      if (disableSwipe) disableSwipe();
      // Lock body scroll - simple approach that doesn't break map coordinates
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      // Enable swipe navigation
      if (enableSwipe) enableSwipe();
      // Restore body scroll
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [showHistoryViewer, enableSwipe, disableSwipe]);

  // Track if user has manually moved the map after vehicle selection
  const [userHasMovedMap, setUserHasMovedMap] = useState(false);
  const [lastSelectedVehicleId, setLastSelectedVehicleId] = useState(null);
  const [initialVehicleCenter, setInitialVehicleCenter] = useState(null);
  const [allowCentering, setAllowCentering] = useState(false);

  // Van routes state
  const [vanRoutes, setVanRoutes] = useState([]); // All available van routes
  const [visibleRoutes, setVisibleRoutes] = useState(new Set()); // Set of visible route IDs
  const [vehicleRouteMap, setVehicleRouteMap] = useState({}); // Maps vehicle ID to route ID
  const [showRoutesSidebar, setShowRoutesSidebar] = useState(false);

  // Ref to control map centering directly
  const centerMapRef = useRef(null);

  // Handler for "Locate Me" button that enables centering temporarily
  const handleLocateMeClick = async () => {
    if (onLocateMeClick) {
      // Enable centering for the location request
      setAllowCentering(true);

      // Call the handler
      await onLocateMeClick();

      // Disable centering after completion
      setTimeout(() => {
        setAllowCentering(false);
      }, 1000);
    }
  };

  // Fetch active van routes on component mount
  useEffect(() => {
    const fetchVanRoutes = async () => {
      try {
        const response = await axios.get(`${API}/van-routes/active`);
        setVanRoutes(response.data);
        // Don't show routes by default for faster loading
        setVisibleRoutes(new Set());
      } catch (error) {
      }
    };

    fetchVanRoutes();
  }, []);

  // Handler for showing/hiding routes when clicking van markers
  const handleShowRoute = async (vehicleId, currentRoute) => {
    if (currentRoute) {
      // Hide the route
      setVisibleRoutes(prev => {
        const newSet = new Set(prev);
        newSet.delete(currentRoute.id);
        return newSet;
      });
      setVehicleRouteMap(prev => {
        const newMap = { ...prev };
        delete newMap[vehicleId];
        return newMap;
      });
    } else {
      // Need to detect which route this van is on
      const detectedRoute = await detectVanRoute(vehicleId);
      if (detectedRoute) {
        // Show the route
        setVisibleRoutes(prev => new Set([...prev, detectedRoute.id]));
        setVehicleRouteMap(prev => ({
          ...prev,
          [vehicleId]: detectedRoute.id
        }));
      }
    }
  };

  // Simple route detection: find closest route to van's current location
  const detectVanRoute = async (vehicleId) => {
    const vehicle = safeVehicles.find(v => v.id === vehicleId);
    if (!vehicle || !vehicle.last_location) return null;

    const vanLat = vehicle.last_location.latitude;
    const vanLng = vehicle.last_location.longitude;

    // Find the closest route by checking distance to all route points
    let closestRoute = null;
    let minDistance = Infinity;

    vanRoutes.forEach(route => {
      route.coordinates.forEach(coord => {
        const distance = Math.sqrt(
          Math.pow(coord.lat - vanLat, 2) + Math.pow(coord.lng - vanLng, 2)
        );
        if (distance < minDistance) {
          minDistance = distance;
          closestRoute = route;
        }
      });
    });

    // Only return route if van is reasonably close (within ~0.01 degrees ≈ 1km)
    if (minDistance < 0.01) {
      return closestRoute;
    }

    // If no route detected, let user choose from available routes
    if (vanRoutes.length > 0) {
      // For now, just show the first available route
      // TODO: Add a route selection modal for better UX
      return vanRoutes[0];
    }

    return null;
  };

  // Handler for toggling routes from sidebar
  const handleToggleRouteFromSidebar = (routeId) => {
    setVisibleRoutes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(routeId)) {
        newSet.delete(routeId);
      } else {
        newSet.add(routeId);
      }
      return newSet;
    });
  };

  const belizeCenter = APP_CONSTANTS.MAP.BELIZE_CENTER;
  const defaultZoom = APP_CONSTANTS.MAP.DEFAULT_ZOOM;

  // Helper function to check if bus/van should be hidden due to inactivity
  const shouldHideInactiveVehicle = (vehicle, isUserOwned = false) => {
    // Never hide vehicles owned by the user
    if (isUserOwned) {
      return false;
    }

    // Only apply to buses and vans
    if (vehicle.vehicle_type !== 'bus' && vehicle.vehicle_type !== 'van') {
      return false;
    }

    if (!vehicle.last_updated) return false;

    const now = new Date();
    let updateTime;

    if (typeof vehicle.last_updated === 'string') {
      if (vehicle.last_updated.endsWith('Z')) {
        updateTime = new Date(vehicle.last_updated);
      } else if (vehicle.last_updated.includes('+00:00')) {
        updateTime = new Date(vehicle.last_updated);
      } else if (vehicle.last_updated.includes('T')) {
        updateTime = new Date(vehicle.last_updated + 'Z');
      } else {
        updateTime = new Date(vehicle.last_updated);
      }
    } else {
      updateTime = new Date(vehicle.last_updated);
    }

    if (isNaN(updateTime.getTime())) return false;

    const diffMilliseconds = now.getTime() - updateTime.getTime();
    const diffMinutes = Math.floor(diffMilliseconds / (1000 * 60));

    // Hide if inactive for 60+ minutes (1 hour)
    return diffMinutes >= 60;
  };

  // Create a Set of user vehicle IDs for quick lookup
  const userVehicleIds = new Set(userVehicles.map(v => v.id));

  // Ensure vehicles is always an array and filter out inactive vehicles
  const safeVehicles = (vehicles || []).filter(vehicle => {
    if (vehicle.is_active === false) return false;

    // Check if vehicle is user-owned
    const isUserOwned = userVehicleIds.has(vehicle.id);

    // Hide buses/vans that haven't moved for 1+ hour (unless user-owned)
    if (shouldHideInactiveVehicle(vehicle, isUserOwned)) return false;
    return true;
  });
  const safeSubscribedBuses = subscribedBusesWithDistance || [];

  // Track if we've already attempted to get user location
  const hasAttemptedLocation = useRef(false);

  // Performance monitoring for map rendering
  const [renderMetrics, setRenderMetrics] = useState({
    totalVehicles: 0,
    clustersRendered: 0,
    renderTime: 0,
    lastUpdate: Date.now()
  });

  useEffect(() => {
    // Handle map resize when entering/exiting fullscreen
    const timer = setTimeout(() => {
      if (mapRef.current) {
        const map = mapRef.current;
        if (map && map.invalidateSize) {
          map.invalidateSize();
        }
      }
    }, 100); // Small delay to ensure CSS transition completes

    return () => clearTimeout(timer);
  }, [isFullscreen]);

  // Add keyboard support for exiting fullscreen
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  /**
   * Gets user location without centering the map
   * Uses browser geolocation API directly
   */
  const getUserLocationOnly = () => {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userPos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        //

        // Call the parent's function to set user location without centering
        if (onSetUserLocation) {
          onSetUserLocation(userPos);
        }
      },
      (error) => {
      },
      {
        timeout: 5000,
        enableHighAccuracy: true,
        maximumAge: 60000 // Cache for 1 minute
      }
    );
  };

  /**
   * Determines if the current map center is the default Belize center
   */
  const isUsingDefaultCenter = () => {
    if (!mapCenter) return true;

    // Handle both object {lat, lng} and array [lat, lng] formats
    if (mapCenter.lat !== undefined && mapCenter.lng !== undefined) {
      // Object format: {lat, lng}
      return mapCenter.lat === belizeCenter[0] && mapCenter.lng === belizeCenter[1];
    } else if (Array.isArray(mapCenter)) {
      // Array format: [lat, lng]
      return mapCenter[0] === belizeCenter[0] && mapCenter[1] === belizeCenter[1];
    }

    // Unknown format, assume it's custom
    return false;
  };

  // Handle explicit center requests via shouldAutoCenter flag
  useEffect(() => {
    if (shouldAutoCenter && setShouldAutoCenter) {
      // Enable centering temporarily
      setAllowCentering(true);

      // Reset the flag after triggering
      setShouldAutoCenter(false);

      // Reset user moved flag to allow tracking
      setUserHasMovedMap(false);

      // Disable centering after a short delay to prevent re-centering
      setTimeout(() => {
        setAllowCentering(false);
      }, 1000); // 1 second should be enough for the MapController to do its work
    }
  }, [shouldAutoCenter, setShouldAutoCenter]);

  // Track vehicle selection changes (but don't auto-center)
  useEffect(() => {
    if (selectedVehicleId !== lastSelectedVehicleId) {
      setLastSelectedVehicleId(selectedVehicleId);

      // If clearing selection, preserve user's current map position
      if (!selectedVehicleId) {
        setInitialVehicleCenter(null);
        // Keep userHasMovedMap as-is - don't reset user's navigation choice
        return;
      }

      // Store the initial center when a vehicle is selected (but don't center)
      if (mapCenter) {
        setInitialVehicleCenter(mapCenter);
      }
    }
  }, [selectedVehicleId, lastSelectedVehicleId, mapCenter]);

  // Auto-location logic on component mount
  // Always center on Belmopan (BELIZE_CENTER), but still get user location for marker
  useEffect(() => {
    if (hasAttemptedLocation.current) return;
    hasAttemptedLocation.current = true;

    // Always get user location for marker display only (don't center on it)
    getUserLocationOnly();

    // Map will use BELIZE_CENTER (Belmopan) as the default center
    // No need to call onAutoLocate - we want to stay centered on Belmopan
  }, []); // Empty dependency array - runs once on mount

  return (
    <div className={`bg-white rounded-xl shadow-lg border border-blue-100 ${isFullscreen
      ? 'fixed inset-0 z-[9999] p-0 rounded-none border-0'
      : 'p-3 sm:p-6'
      }`}>
      {/* Map Header - Compact on mobile */}
      {!isFullscreen && (
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 md:mb-6">
          <div className="mb-3 md:mb-0">
            <div className="flex items-center space-x-3 md:space-x-4">
              <div className="bg-gradient-to-br from-blue-500 to-cyan-500 p-2 md:p-3 rounded-lg md:rounded-xl shadow-md">
                <span className="text-xl md:text-2xl">🚛</span>
              </div>
              <div>
                <h2 className="text-lg md:text-2xl font-bold text-blue-900">Live Truck Map</h2>
                <p className="text-xs md:text-sm text-blue-600 mt-0.5 md:mt-1 hidden sm:block">
                  Real-time GPS tracking
                </p>
              </div>
              {enableClustering && safeVehicles.length > maxMarkersBeforeClustering && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-blue-600">🚀</span>
                    <div>
                      <div className="text-sm font-medium text-blue-800">
                        High Performance Mode
                      </div>
                      <div className="text-xs text-blue-600">
                        {safeVehicles.length} trucks clustered for smooth performance
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Auto-locate Button */}
            {/* <button
              onClick={onAutoLocate}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <span className="mr-2">📍</span>
              <span className="hidden sm:inline">My Location</span>
              <span className="sm:hidden">Locate Me</span>
            </button> */}

            {/* Performance Toggle */}
            {/* 
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-gray-600">Clustering:</span>
              <button
                onClick={() => {/* This would be controlled by parent component END COMMENT HERE }}
                className={`px-2 py-1 rounded text-xs ${enableClustering
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
                  }`}
                title={enableClustering ? 'Clustering enabled for performance' : 'Individual markers shown'}
              >
                {enableClustering ? 'ON' : 'OFF'}
              </button>
            </div> */}

            {/* Re-center on vehicle button (when user has moved map) */}
            {selectedVehicleId && userHasMovedMap && (
              <button
                onClick={() => {
                  //
                  setUserHasMovedMap(false);
                  setAllowCentering(true);
                  // Disable centering after re-center is complete
                  setTimeout(() => {
                    setAllowCentering(false);
                  }, 1000);
                }}
                className="px-4 py-2 bg-blue-100 text-blue-800 text-sm rounded-md hover:bg-blue-200 transition-colors flex items-center"
                title="Re-center map on selected vehicle"
              >
                <span className="mr-1">🎯</span>
                Track Vehicle
              </button>
            )}

            {/* Clear Selection Button */}
            {selectedVehicleId && (
              <button
                onClick={() => onVehicleSelect(null)}
                className="px-4 py-2 bg-red-100 text-red-800 text-sm rounded-md hover:bg-red-200 transition-colors"
              >
                Clear Selection
              </button>
            )}
          </div>
        </div>
      )}
      {/* Did The Truck Pass My House? - Primary Feature - Above map */}
      {!isFullscreen && (
        <div className="mb-4">
          <button
            onClick={() => {
              setCheckAllTrucksMode(true);
              setSelectedTruckForHistory(null);
              setShowHistoryViewer(true);
            }}
            className="w-full px-4 py-3 rounded-lg font-medium text-base transition-colors flex items-center justify-center bg-gradient-to-r from-green-600 to-teal-600 text-white hover:from-green-700 hover:to-teal-700 shadow-lg"
          >
            <span className="mr-2 text-xl">🏠</span>
            Did a Truck Pass My House Today?
          </button>
        </div>
      )}

      {/* Interactive Map Container - Taller on mobile for better usability */}
      <div className={`relative rounded-lg overflow-hidden ${isFullscreen
        ? 'h-screen'
        : 'h-[60vh] min-h-[300px] max-h-[500px] md:h-96'
        }`}>
        <StopSwipeBubble>
          <MapContainer
            ref={mapRef}
            center={
              mapCenter
                ? (mapCenter.lat !== undefined ? [mapCenter.lat, mapCenter.lng] : mapCenter)
                : belizeCenter
            }
            zoom={mapZoom || defaultZoom}
            style={{ height: '100%', width: '100%' }}
            zoomControl={true}
            whenReady={() => {
              //

              // Set centerMapRef to allow direct map control
              centerMapRef.current = true;

              // Set up event listeners for user map interactions
              if (mapRef.current) {
                const map = mapRef.current;

                // Detect when user manually drags or zooms the map
                const handleUserInteraction = () => {
                  if (!userHasMovedMap) {
                    //
                    setUserHasMovedMap(true);
                  }
                };

                // Listen for drag and zoom events
                map.on('dragend', handleUserInteraction);
                map.on('zoomend', handleUserInteraction);
              }
            }}
          >
            {/* MapController - Re-enabled for proper centering with fallbacks */}
            <MapController
              center={
                mapCenter
                  ? (mapCenter.lat !== undefined ? [mapCenter.lat, mapCenter.lng] : mapCenter)
                  : belizeCenter
              }
              zoom={mapZoom}
              disabled={!allowCentering} // Only allow centering when explicitly requested
            />

            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* User Location Marker */}
            {userLocation && (
              <Marker
                position={[userLocation.lat, userLocation.lng]}
                icon={getUserLocationIcon()}
              >
                <Popup>
                  <div className="text-sm">
                    <h3 className="font-bold text-blue-600 mb-2 flex items-center">
                      <span className="mr-2">📍</span>
                      Your Current Location
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-gray-600 text-xs"><strong>Coordinates:</strong></p>
                        <p className="font-mono text-xs">
                          {userLocation.lat.toFixed(6)}, {userLocation.lng.toFixed(6)}
                        </p>
                      </div>

                      {userLocation.accuracy && (
                        <div>
                          <p className="text-gray-600 text-xs"><strong>Accuracy:</strong></p>
                          <p className="text-xs">±{Math.round(userLocation.accuracy)} meters</p>
                        </div>
                      )}

                      <div className="pt-2 border-t border-blue-200">
                        <p className="text-blue-600 font-medium text-xs flex items-center">
                          🎯 <span className="ml-1">Your current GPS position</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Optimized Vehicle Markers with Clustering */}
            {enableClustering ? (
              <OptimizedVehicleMarkers
                vehicles={safeVehicles}
                selectedVehicleId={selectedVehicleId}
                onVehicleSelect={onVehicleSelect}
                getTimeSinceUpdate={getTimeSinceUpdate}
                maxMarkersBeforeClustering={maxMarkersBeforeClustering}
                userVehicles={userVehicles}
              />
            ) : (
              // Fallback to individual markers for small datasets or when clustering disabled
              safeVehicles.map((vehicle) => {
                const routeId = vehicleRouteMap[vehicle.id];
                const currentRoute = routeId ? vanRoutes.find(r => r.id === routeId) : null;

                return (
                  <VehicleMarker
                    key={`${vehicle.id}-${vehicle.last_updated || Date.now()}`}
                    vehicle={vehicle}
                    selectedVehicleId={selectedVehicleId}
                    onVehicleSelect={onVehicleSelect}
                    getTimeSinceUpdate={getTimeSinceUpdate}
                    onShowRoute={handleShowRoute}
                    currentRoute={currentRoute}
                  />
                );
              })
            )}

            {/* Van Route Polylines */}
            {vanRoutes
              .filter(route => visibleRoutes.has(route.id))
              .map(route => (
                <React.Fragment key={route.id}>
                  {/* Route polyline */}
                  <Polyline
                    positions={route.coordinates.map(coord => [coord.lat, coord.lng])}
                    color={route.color}
                    weight={4}
                    opacity={0.7}
                  />

                  {/* Directional arrows */}
                  {generateRouteArrows(route.coordinates, route.color).map((arrow, idx) => (
                    <Marker
                      key={`arrow-${route.id}-${idx}`}
                      position={arrow.position}
                      icon={createRouteArrowIcon(arrow.rotation, arrow.color)}
                      interactive={false}
                    />
                  ))}

                  {/* Route Stops */}
                  {route.stops && route.stops
                    .filter(stop => stop.is_active)
                    .sort((a, b) => a.sequence - b.sequence)
                    .map(stop => (
                      <Marker
                        key={`stop-${route.id}-${stop.id}`}
                        position={[stop.position.lat, stop.position.lng]}
                        icon={createStopIcon(route.color, stop.sequence)}
                      >
                        <Popup className="stop-popup">
                          <div className="min-w-[200px]">
                            <div className="flex items-center mb-2">
                              <span
                                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2"
                                style={{ backgroundColor: route.color }}
                              >
                                {stop.sequence}
                              </span>
                              <h3 className="font-bold text-gray-900">{stop.name}</h3>
                            </div>

                            {stop.description && (
                              <p className="text-sm text-gray-600 mb-2">{stop.description}</p>
                            )}

                            <div className="text-xs text-gray-500 border-t pt-2 mt-2">
                              <div className="flex items-center mb-1">
                                <span className="mr-1">🚛</span>
                                <span className="font-medium">{route.name}</span>
                              </div>

                              {stop.schedule && stop.schedule.length > 0 && (
                                <div className="mt-2">
                                  <div className="font-medium text-gray-700 mb-1">Schedule:</div>
                                  {stop.schedule.map((sched, idx) => (
                                    <div key={idx} className="flex justify-between text-xs">
                                      <span>{sched.time}</span>
                                      <span className="text-gray-400">{sched.days?.join(', ')}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    ))
                  }
                </React.Fragment>
              ))
            }
          </MapContainer>
          {/* Fullscreen Controls - Toggle and Auto-locate buttons */}
          <div className="absolute top-2 right-2 z-[1000] flex items-center space-x-2">
            {/* Re-center on vehicle button (fullscreen) */}
            {selectedVehicleId && userHasMovedMap && (
              <button
                onClick={() => {
                  //
                  setUserHasMovedMap(false);
                  setAllowCentering(true);
                  // Disable centering after re-center is complete
                  setTimeout(() => {
                    setAllowCentering(false);
                  }, 1000);
                }}
                className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-lg"
                title="Re-center map on selected vehicle"
                aria-label="Re-center map on selected vehicle"
              >
                <span className="mr-1">🎯</span>
                <span className="hidden sm:inline text-sm">Track Vehicle</span>
                <span className="sm:hidden text-sm">Track</span>
              </button>
            )}

            {selectedVehicleId && (
              <button
                onClick={() => onVehicleSelect(null)}
                className="px-4 py-2 bg-red-100 text-red-800 text-sm rounded-md hover:bg-red-200 transition-colors"
              >
                Clear Selection
              </button>
            )}
            {/* Auto-locate Button */}
            <button
              onClick={handleLocateMeClick}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-lg"
              title="Center map on my location"
              aria-label="Center map on my location"
            >
              <span className="mr-1">📍</span>
              <span className="hidden sm:inline text-sm">My Location</span>
              <span className="sm:hidden text-sm">Locate Me</span>
            </button>

            {/* Fullscreen Toggle Button */}
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="bg-white hover:bg-gray-50 border border-gray-300 rounded-md p-2 shadow-lg transition-colors duration-200"
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? (
                // Exit fullscreen icon (compress/minimize)
                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              ) : (
                // Enter fullscreen icon (expand)
                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8l4-4m0 0h4M8 4v4m8-4l4 4m0 0v4m0-4h-4m4 16l-4-4m0 0v-4m0 4h-4m-4 4v-4m0 0H4m4 0l-4-4" />
                </svg>
              )}
            </button>
          </div>

          {/* Garbage Truck Routes Toggle Button - Bottom Left (only for developer) */}
          {isDeveloper && vanRoutes.length > 0 && (
            <div className="absolute bottom-2 left-2 z-[1000]">
              <button
                onClick={() => setShowRoutesSidebar(!showRoutesSidebar)}
                className={`px-3 py-2 rounded-md shadow-lg transition-colors ${
                  showRoutesSidebar || visibleRoutes.size > 0
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
                title="Toggle Garbage Truck Routes"
              >
                <span className="mr-1">🚛</span>
                <span className="text-sm">Routes</span>
                {visibleRoutes.size > 0 && (
                  <span className="ml-1 bg-white text-blue-600 rounded-full px-2 py-0.5 text-xs font-bold">
                    {visibleRoutes.size}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Garbage Truck Routes Sidebar (only for developer) */}
          {isDeveloper && showRoutesSidebar && vanRoutes.length > 0 && (
            <div className="absolute bottom-14 left-2 z-[1000] bg-white rounded-lg shadow-xl border border-gray-200 w-64 max-h-96 overflow-y-auto">
              <div className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <span className="mr-2">🚛</span>
                    Garbage Truck Routes
                  </h3>
                  <button
                    onClick={() => setShowRoutesSidebar(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-2">
                  {vanRoutes.map(route => (
                    <div
                      key={route.id}
                      className={`p-3 rounded-md border-2 transition-all cursor-pointer ${
                        visibleRoutes.has(route.id)
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleToggleRouteFromSidebar(route.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 flex-1">
                          <div
                            className="w-4 h-4 rounded-full border-2 border-white shadow"
                            style={{ backgroundColor: route.color }}
                          ></div>
                          <span className="font-medium text-sm text-gray-900">
                            {route.name}
                          </span>
                        </div>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          visibleRoutes.has(route.id)
                            ? 'bg-blue-600 border-blue-600'
                            : 'bg-white border-gray-300'
                        }`}>
                          {visibleRoutes.has(route.id) && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      {route.description && (
                        <p className="text-xs text-gray-600 mt-1 ml-6">{route.description}</p>
                      )}
                    </div>
                  ))}
                </div>

                {visibleRoutes.size > 0 && (
                  <button
                    onClick={() => setVisibleRoutes(new Set())}
                    className="w-full mt-3 px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 text-sm font-medium"
                  >
                    Hide All Routes
                  </button>
                )}
              </div>
            </div>
          )}
        </StopSwipeBubble>
      </div>

      {/* Subscribed Buses List - Below map */}
      {!isFullscreen && (
        <div className="mt-4">
          <SubscribedBusesList
            subscribedBuses={safeSubscribedBuses}
            onBusClick={onSubscribedBusClick}
            onCreateSubscription={onCreateSubscription}
          />
        </div>
      )}

      {/* History Viewer Modal - supports both single truck and all trucks mode */}
      {showHistoryViewer && (selectedTruckForHistory || checkAllTrucksMode) && (
        <div
          className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
          onClick={(e) => {
            // Close modal when clicking the backdrop
            if (e.target === e.currentTarget) {
              setShowHistoryViewer(false);
              setSelectedTruckForHistory(null);
              setCheckAllTrucksMode(false);
            }
          }}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-2xl"
            style={{ overscrollBehavior: 'contain' }}
            onClick={(e) => e.stopPropagation()}
          >
            <TruckHistoryViewer
              vehicleId={selectedTruckForHistory?.id}
              vehicleName={selectedTruckForHistory ? `${selectedTruckForHistory.make} ${selectedTruckForHistory.model}` : null}
              checkAllTrucks={checkAllTrucksMode}
              onClose={() => {
                setShowHistoryViewer(false);
                setSelectedTruckForHistory(null);
                setCheckAllTrucksMode(false);
              }}
            />
          </div>
        </div>
      )}

      {/* Map Instructions */}
      {!isFullscreen && (
        <div className="text-center text-sm text-blue-700 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200 mt-4">
          <p className="font-medium mb-2 text-blue-800">📱 Map Controls:</p>
          <div className="space-y-1 text-xs text-blue-600">
            <p>• <strong>Click markers</strong> to view garbage truck information</p>
            <p>• <strong>Click "Locate Me"</strong> to center map on your position</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapView;